import { useMutation, useQueries, useQuery } from 'react-query';
import { QueryServerKeys } from 'QueryServerKeys';
import {
  getChartJobsData,
  getJobLogs,
  getJobMetrics,
  getSchemasAndTables,
  getSignedURL,
  getStatusCounts,
  killJob,
  getJobDetail,
} from 'services/jobs';
import { useContext } from 'react';
import { AppContext } from 'contexts/AppContext';
import { useOnErrorLogout } from './user';
import { JobDetailsSidebarProps } from 'components/JobsTable/JobsDetailsSidebar/JobDetailsSidebarHelpers';
import { DetailedSparkJobDTO } from 'types/types';
import {
  fetchJobsPaginatedPage,
  getJobsPaginatedQueryKey,
} from 'utils/jobsQueryCache';

export function useSchemasAndTables() {
  const queryOptions = useQuery(
    [QueryServerKeys.LISTS.GET_SCHEMAS_AND_TABLES],
    async () => {
      const res = await getSchemasAndTables();
      return res.data;
    },
    {
      refetchInterval: 1000 * 60,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );
  return queryOptions;
}

export function useGetJobLogs({
  clusterName,
  jobId,
  mV,
  table,
  signature,
  expires,
  schema,
}: {
  clusterName: string;
  jobId: string;
  mV?: string;
  table?: string;
  signature?: string;
  expires?: string;
  schema?: string;
}) {
  const queryOptions = useMutation(
    async () => {
      return getJobLogs({
        clusterName,
        jobId,
        ...(mV ? { mV } : {}),
        ...(table ? { table } : {}),
        signature,
        expires,
        ...(schema ? { schema } : {}),
      });
    },
    {
      mutationKey: [QueryServerKeys.JOBS.GET_JOB_LOGS, clusterName, jobId],
      onError(error: string | undefined) {
        throw error;
      },
    }
  );
  return queryOptions;
}

export function useGetSignedURL({
  clusterName,
  jobId,
}: {
  clusterName: string;
  jobId?: string;
}) {
  const queryOptions = useMutation(
    async ({
      tableName,
      schema,
      scope,
    }: {
      tableName: string;
      schema: string;
      scope: string;
    }) => {
      return getSignedURL({ clusterName, tableName, schema, scope, jobId });
    },
    {
      mutationKey: [
        QueryServerKeys.JOBS.GET_JOB_SIGNED_URL,
        clusterName,
        jobId,
      ],
      onError(error: string | undefined) {
        throw new Error(error);
      },
    }
  );
  return queryOptions;
}

export function useGetJobMetrics({ jobId }: { jobId: string }) {
  const queryOptions = useQuery(
    [QueryServerKeys.JOBS.GET_JOB_METRICS, jobId],
    () => getJobMetrics({ sparkJobId: jobId }),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      select(res) {
        const totalRunTime = res.data
          ?.map((d: any) => d?.['Task Metrics']['Executor Run Time'])
          .reduce(
            (accumulator: number, currentValue: number) =>
              accumulator + currentValue,
            0
          );
        const totalCPUTime = res.data
          ?.map((d: any) => d?.['Task Metrics']['Executor CPU Time'])
          .reduce(
            (accumulator: number, currentValue: number) =>
              accumulator + currentValue,
            0
          );
        const shuffleTime = res.data
          ?.map(
            (d: any) =>
              d?.['Task Metrics']['Shuffle Write Metrics']['Shuffle Write Time']
          )
          .reduce(
            (accumulator: number, currentValue: number) =>
              accumulator + currentValue,
            0
          );
        const gcTime = res.data
          ?.map((d: any) => d?.['Task Metrics']['JVM GC Time'])
          .reduce(
            (accumulator: number, currentValue: number) =>
              accumulator + currentValue,
            0
          );
        const otherTime =
          totalRunTime -
          totalCPUTime / 1000000 -
          shuffleTime / 1000000 -
          gcTime;
        return { totalRunTime, totalCPUTime, shuffleTime, gcTime, otherTime };
      },
    }
  );
  return queryOptions;
}

export function useKillJob() {
  const queryOptions = useMutation(
    async ({
      clusterName,
      sparkJobID,
    }: {
      clusterName: string;
      sparkJobID: string;
    }) => {
      return await killJob({ clusterName, sparkJobID });
    },
    {
      onError(error: string | undefined) {
        throw new Error(error);
      },
    }
  );
  return queryOptions;
}

const commonRefetchProps = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
};

export function useStatusCounts({
  clusterName,
  isEnabled,
}: {
  clusterName: string;
  isEnabled: boolean;
}) {
  const onErrorLogout = useOnErrorLogout({ clusterName });
  const queryOptions = useQuery(
    [QueryServerKeys.JOBS.GET_STATUS_COUNTS, clusterName],
    () => getStatusCounts({ clusterName }),
    {
      enabled: isEnabled,
      ...onErrorLogout,
      ...commonRefetchProps,
    }
  );
  return queryOptions;
}

export function useJobsAndJobsStatuses({
  clusterName,
  pageNumber,
  pageSize,
  isJobsEnabled,
  isJobsStatusesEnabled,
  tableName,
  schemaName,
  status,
  sortBy,
  sortOrder,
  isSignedURL,
  expires,
  signature,
  prefix,
  end_date,
  start_date,
}: {
  clusterName: string;
  pageNumber: number;
  pageSize: number;
  isJobsEnabled: boolean;
  isJobsStatusesEnabled: boolean;
  tableName?: string | string[];
  schemaName?: string | string[];
  status?: string | string[];
  sortBy?: string | null;
  sortOrder?: string | null;
  isSignedURL?: boolean;
  expires?: string;
  signature?: string;
  prefix?: string;
  end_date?: string;
  start_date?: string;
}) {
  const onErrorLogout = useOnErrorLogout({ clusterName });

  return useQueries([
    {
      queryKey: [QueryServerKeys.JOBS.GET_STATUS_COUNTS, clusterName],
      queryFn: () => getStatusCounts({ clusterName }),
      enabled: isJobsStatusesEnabled,
      ...onErrorLogout,
      ...commonRefetchProps,
    },
    {
      queryKey: getJobsPaginatedQueryKey({
        clusterName,
        pageNumber,
        pageSize,
        sortBy,
        sortOrder,
        tableName,
        schemaName,
        start_date,
        end_date,
        status,
      }),
      queryFn: () =>
        fetchJobsPaginatedPage({
          clusterName,
          pageNumber,
          pageSize,
          isSignedURL,
          sortBy,
          sortOrder,
          tableName,
          schemaName,
          status,
          start_date,
          end_date,
          expires,
          signature,
          prefix,
        }),
      enabled: isJobsEnabled,
      ...onErrorLogout,
      ...commonRefetchProps,
    },
  ]);
}

export function useGetChartJobsData({
  tableName,
  schemaName,
  clusterName,
  isSignedURL = false,
  expires,
  signature,
  prefix,
}: {
  tableName: string;
  schemaName: string;
  clusterName: string;
  isSignedURL?: boolean;
  expires?: string;
  signature?: string;
  prefix?: string;
}) {
  const { currentAppliedFilters } = useContext(AppContext);
  const queryOptions = useQuery(
    [QueryServerKeys.JOBS.GET_JOBS_CHART, clusterName, schemaName, tableName],
    async () => {
      const res = await getChartJobsData({
        tableName,
        schemaName,
        clusterName,
        ...(isSignedURL && expires ? { expires } : {}),
        ...(isSignedURL && signature ? { signature } : {}),
        ...(isSignedURL && prefix ? { prefix } : {}),
        ...currentAppliedFilters,
      });
      return res?.Jobs.map((job: any) => ({
        sparkJobId: job.SparkJobID,
        duration: job.Duration,
        queueingTime: job.QueueingTime,
        createdAt: job.CreatedAt,
        clusterPrefix: job.ClusterPrefix,
        clusterUrl: job.ClusterUrl,
        sparkJobStatus: job.SparkJobStatus,
        sparkAppId: job.SparkAppID,
        sparkAppName: job.SparkAppName,
        mvLanguage: job.MVLanguage,
        vendor: job.Vendor,
        schemaName: job.SchemaName,
        tableName: job.TableName,
        incortaGuid: job.IncortaGUID,
        totalExecutorCount: job.TotalExecutorCount,
        peakMemoryInMB: job.PeakMemoryInMB,
        sparkDriverMemoryInMB: job.SparkDriverMemoryInMB,
        clusterName: job.ClusterName,
        code: job.SparkExitCode,
      }));
    },
    {
      refetchInterval: 1000 * 60,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );
  return queryOptions;
}

export function useGetJobDetail({
  jobId,
  schemaName,
  isEnabled,
  isSignedURL = false,
  expires,
  signature,
  prefix,
  clusterName,
}: {
  jobId: string;
  schemaName?: string;
  isEnabled: boolean;
  isSignedURL?: boolean;
  expires?: string;
  signature?: string;
  prefix?: string;
  clusterName?: string;
}) {
  const queryOptions = useQuery(
    [QueryServerKeys.JOBS.GET_JOB_DETAIL, jobId],
    async () => {
      const res = await getJobDetail({
        jobId,
        ...(isSignedURL && schemaName ? { schemaName } : {}),
        ...(isSignedURL && expires ? { expires } : {}),
        ...(isSignedURL && signature ? { signature } : {}),
        ...(isSignedURL && prefix ? { prefix } : {}),
        ...(isSignedURL && clusterName ? { clusterName } : {}),
      });
      return res?.data;
    },
    {
      onError: (error: string | undefined) => {
        console.error('Error fetching job detail:', error);
      },
      enabled: isEnabled,
    }
  );
  return queryOptions;
}
